import { useEffect, useState } from 'react';
import type { ProjectMemberItem } from '@hygge/shared';
import { Button } from '@/components/ui/button';
import { DatePicker } from '@/components/ui/date-picker';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useReopenMemberPeriod } from '@/hooks/use-projects';
import { toDateParam } from '@/lib/date';
import { useEmployeeNameFormatter } from '@/lib/employee-name';
import { useNow } from '@/hooks/use-now';
import {
  REOPEN_DEFAULT_DURATION,
  currentWindowLabel,
  reopenDurationOptions,
  reopenPickerBounds,
} from '@/features/time-tracker/lib/reopen-window';

/**
 * Reopen a closed period for one member of this project: From (the first entry
 * day covered — by default a week ago while that is still last month, else
 * last month's final week; the 1st of last month at the earliest, today at the
 * latest) and how long. Bounds and defaults are re-read each time the dialog
 * opens, so a tab left open past midnight UTC offers the new day.
 * A member who already has a live window is told so: opening again replaces
 * it rather than extending it. [HW-963]
 */
export function ReopenPeriodDialog({
  projectId,
  member,
  onOpenChange,
}: {
  projectId: string;
  member: ProjectMemberItem | null;
  onOpenChange: (open: boolean) => void;
}) {
  const formatName = useEmployeeNameFormatter();
  const reopen = useReopenMemberPeriod(projectId);
  const [bounds, setBounds] = useState(() => reopenPickerBounds(new Date()));
  const [from, setFrom] = useState<Date | null>(bounds.initial);
  const [hours, setHours] = useState(REOPEN_DEFAULT_DURATION);
  const now = useNow(30_000);
  const current = currentWindowLabel(member?.closedPeriod, now);

  useEffect(() => {
    if (!member) return;
    const next = reopenPickerBounds(new Date());
    setBounds(next);
    setFrom(next.initial);
    setHours(REOPEN_DEFAULT_DURATION);
  }, [member]);

  const handleOpen = async () => {
    const day = from ? toDateParam(from) : null;
    if (!member || !day) return;
    try {
      await reopen.mutateAsync({ employeeId: member.employeeId, from: day, hours: Number(hours) });
      onOpenChange(false);
    } catch {
      // The global mutation toast names the refusal; the dialog stays open.
    }
  };

  return (
    <Dialog open={!!member} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Reopen closed period</DialogTitle>
          <DialogDescription>
            {member ? formatName({ firstName: member.firstName, lastName: member.lastName }) : ''} can add, edit
            and delete their own entries on this project from the chosen day to today, until the time runs out.
          </DialogDescription>
        </DialogHeader>

        {current && <p className="text-sm text-muted-foreground">{current}</p>}

        <div className="space-y-4 py-2">
          <div className="space-y-1.5">
            <Label htmlFor="reopen-from" className="text-sm font-medium">
              From
            </Label>
            <DatePicker
              id="reopen-from"
              value={from}
              onChange={setFrom}
              minDate={bounds.min}
              maxDate={bounds.max}
              disabled={reopen.isPending}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="reopen-duration" className="text-sm font-medium">
              Duration
            </Label>
            <Select value={hours} onValueChange={setHours} disabled={reopen.isPending}>
              <SelectTrigger id="reopen-duration">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {reopenDurationOptions().map((o) => (
                  <SelectItem key={o.value} value={o.value}>
                    {o.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} className="cursor-pointer">
            Cancel
          </Button>
          <Button onClick={handleOpen} disabled={!from || reopen.isPending} className="cursor-pointer">
            {reopen.isPending ? 'Opening...' : 'Open'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
